/**
 * Web Push Notifications for Admin Dashboard
 * Uses Firebase Cloud Messaging (FCM) to deliver real-time push notifications
 * to the admin's browser even when the tab is in the background.
 */

import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { app, db } from './firebase';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;
const FCM_TOKENS_DOC = 'config/adminFCMTokens';

/**
 * Validate VAPID key format (must be base64url-encoded P-256 public key)
 * Valid lengths: 65 bytes (uncompressed: 0x04 || X || Y) or 33 bytes (compressed: 0x02/0x03 || X)
 * Firebase typically uses 65-byte format but 63-byte base64url-encoded compressed (87 chars) also works
 */
function isValidVapidKey(key: string): boolean {
  try {
    const decoded = Uint8Array.from(atob(key.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0));
    return decoded.byteLength === 65 || decoded.byteLength === 33;
  } catch {
    return false;
  }
}

/**
 * Request notification permission and register FCM token.
 * Stores the token in Firestore so Cloud Functions can send to it.
 * Returns the FCM token, or null if permission denied / not supported.
 */
export async function requestPushPermission(adminUid: string): Promise<string | null> {
  try {
    const supported = await isSupported();
    if (!supported) {
      console.warn('FCM not supported in this browser');
      return null;
    }

    if (!VAPID_KEY) {
      console.warn('VITE_FIREBASE_VAPID_KEY not set — skipping push registration');
      return null;
    }

    // Validate VAPID key format before attempting registration
    if (!isValidVapidKey(VAPID_KEY)) {
      console.warn('VITE_FIREBASE_VAPID_KEY is invalid (must be a valid P-256 public key) — regenerate in Firebase Console → Project Settings → Cloud Messaging → Web Push certificates');
      return null;
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.warn('Push notification permission denied');
      return null;
    }

    const messaging = getMessaging(app);
    const token = await getToken(messaging, { vapidKey: VAPID_KEY });

    if (token) {
      // Store token in Firestore so Cloud Functions can send to it
      await setDoc(
        doc(db, FCM_TOKENS_DOC),
        {
          [adminUid]: {
            token,
            updatedAt: Timestamp.now(),
            userAgent: navigator.userAgent.substring(0, 100),
          },
        },
        { merge: true }
      );
      console.log('✅ FCM token registered for admin:', adminUid);
    }

    return token;
  } catch (error) {
    console.error('Failed to register push notifications:', error);
    return null;
  }
}

/**
 * Listen for foreground FCM messages (when tab is active).
 * Returns an unsubscribe function.
 */
export async function onForegroundMessage(
  callback: (payload: { title: string; body: string; data?: Record<string, any> }) => void
): Promise<(() => void) | null> {
  try {
    const supported = await isSupported();
    if (!supported) return null;

    const messaging = getMessaging(app);
    const unsubscribe = onMessage(messaging, (payload) => {
      callback({
        title: payload.notification?.title || 'NePro Admin',
        body: payload.notification?.body || '',
        data: payload.data as Record<string, any> | undefined,
      });
    });

    return unsubscribe;
  } catch (error) {
    console.error('Failed to set up foreground message listener:', error);
    return null;
  }
}
